/**
 * Cookie Banner Component
 */
import React, { useState, useEffect } from 'react';
import { Shield, ChevronRight } from 'lucide-react';
import { isIOS } from '../utils/platform';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Hide cookie banner on iOS native app (Apple requirement)
    if (isIOS()) return;
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
  };

  const rejectAll = () => {
    const consentData = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
  };

  const savePreferences = () => {
    const consentData = {
      ...preferences,
      necessary: true,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
    setShowPreferences(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-end sm:items-center justify-center p-4" data-testid="cookie-banner">
      <div className="bg-[#242424] rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {!showPreferences ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#F5A623]/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#F5A623]" />
              </div>
              <h2 className="text-lg font-bold text-white">Informativa Cookie</h2>
            </div>
            
            <p className="text-gray-300 text-sm mb-4 leading-relaxed">
              Utilizziamo cookie tecnici necessari per il funzionamento dell'app e, previo tuo consenso, cookie analitici per migliorare i nostri servizi.
            </p>
            
            <p className="text-gray-400 text-xs mb-6">
              Puoi accettare tutti i cookie, rifiutare quelli non necessari o personalizzare le tue preferenze. 
              Per maggiori informazioni consulta la nostra <a href="/cookie-policy" className="text-[#F5A623] hover:underline">Cookie Policy</a> e <a href="/privacy-policy" className="text-[#F5A623] hover:underline">Privacy Policy</a>.
            </p>
            
            <div className="space-y-3">
              <div className="flex gap-3">
                <button
                  onClick={rejectAll}
                  className="flex-1 bg-[#333] text-white py-3 rounded-xl font-bold text-sm hover:bg-[#444] transition-colors"
                  data-testid="cookie-reject-all"
                >
                  Rifiuta
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 btn-primary py-3 rounded-xl font-bold text-sm"
                  data-testid="cookie-accept-all"
                >
                  Accetta Tutti
                </button>
              </div>
              <button
                onClick={() => setShowPreferences(true)}
                className="w-full text-gray-400 text-sm hover:text-white transition-colors py-2"
                data-testid="cookie-customize"
              >
                Personalizza preferenze
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setShowPreferences(false)} className="text-gray-400 hover:text-white">
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
              <h2 className="text-lg font-bold text-white">Preferenze Cookie</h2>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="bg-[#1A1A1A] rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-white text-sm">Cookie Necessari</span>
                  <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded">Sempre attivi</span>
                </div>
                <p className="text-xs text-gray-400">Essenziali per il funzionamento dell'app. Includono autenticazione e preferenze di sessione.</p>
              </div>
              
              <div className="bg-[#1A1A1A] rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-white text-sm">Cookie Analitici</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#F5A623]"></div>
                  </label>
                </div>
                <p className="text-xs text-gray-400">Ci aiutano a capire come utilizzi l'app per migliorare l'esperienza.</p>
              </div>
              
              <div className="bg-[#1A1A1A] rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-white text-sm">Cookie di Marketing</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#F5A623]"></div>
                  </label>
                </div>
                <p className="text-xs text-gray-400">Utilizzati per mostrarti contenuti e offerte personalizzate.</p>
              </div>
            </div>
            
            <button
              onClick={savePreferences}
              className="w-full btn-primary py-3 rounded-xl font-bold text-sm"
              data-testid="cookie-save-preferences"
            >
              Salva Preferenze
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
